import { useEffect } from 'react'
import { api } from '@api'
import { FetchParams, PaginatedResponse } from './types'
import { usePaginate } from './usePaginate'
import { Scope } from '@utils'

export type Note = {
  id: string
  title?: string
  content: string
  createdByAccountId: string
  contactAccountId: string | null // null for personal notes, accountId for shared notes
  scope: string
  isShared: boolean
  // Pinned notes come first (« À retenir »). Absent on an older API.
  isPinned?: boolean
  // How the note was written.
  source?: 'manual' | 'voice' | 'assistant'
  createdAt: string
  updatedAt: string
}

export type NotesFilters = {
  q?: string
  contactId?: string
  scope?: Scope
  type?: 'personal' | 'shared'
  onlyGhost?: boolean
  noGhost?: boolean
}

// Notes are loaded in pages of 50, and every page is fetched (see below):
// a list must never silently stop at the first page.
const NOTES_PAGE_SIZE = 50

// URLSearchParams encodes the values: a search for "#voyage" used to cut the
// URL at "#" and return every note.
export const buildNotesUrl = ({
  pageParam = '1',
  q,
  contactId,
  scope,
  type,
  onlyGhost,
  noGhost,
}: FetchParams) => {
  const params = new URLSearchParams({ page: String(pageParam), limit: String(NOTES_PAGE_SIZE) })
  if (q) params.set('q', q)
  if (contactId) params.set('contactId', contactId)
  if (scope) params.set('scope', scope)
  if (type) params.set('type', type) // 'personal' or 'shared'
  if (onlyGhost) params.set('onlyGhost', 'true')
  if (noGhost) params.set('noGhost', 'true')
  return `/notes?${params.toString()}`
}

const fetchNotes = async (params: FetchParams): Promise<PaginatedResponse<Note>> => {
  const { data } = await api.get<PaginatedResponse<Note>>(buildNotesUrl(params))
  return data
}

export const usePaginateNotes = ({
  q,
  contactId,
  scope,
  type,
  onlyGhost,
  noGhost,
}: NotesFilters) => {
  const query = usePaginate<Note>(
    'notes',
    fetchNotes,
    { q, contactId, scope, type, onlyGhost, noGhost },
    { keepPreviousData: true },
  )
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query

  // Load the following pages as soon as one arrives, so the tabs always list
  // (and count) every note, not just the first page. A page that failed is not
  // asked for again by itself (it was, without end, while the server was
  // down): the list offers « Réessayer », and the pages after it follow.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage])

  return query
}
